import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Form, Select, Space, Spin, Table, Typography, message } from "antd";
import {
  BankOutlined,
  DoubleRightOutlined,
  DownOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import "./index.css";

const { Text } = Typography;
const EMPTY_LIST = [];
const CALCULATED_METRICS = new Set(["ENDING_BALANCE_BEFORE_PAYABLES", "PROJECTED_ENDING_BALANCE"]);
const BALANCE_METRICS = new Set(["BEGIN_BANK_BALANCE", "ENDING_BANK_BALANCE", ...CALCULATED_METRICS]);
const MAINTENANCE_METRICS = new Set(["MAINTENANCE_PO_PAYABLES", "MAINTENANCE_MATERIAL_PAYABLES"]);
const PAYABLE_METRICS = new Set(["OUTSTANDING_CHECKS", "APPROVED_ACCOUNTS_PAYABLES", "PENDING_ACCOUNTS_PAYABLES"]);
const displayDate = (value) => value && dayjs(value).isValid() ? dayjs(value).format("MM/DD/YYYY") : "—";

// Reuse the work-order detail route opened from Maintenance Contractor AP statuses.
const maintenanceDetailHref = (record) => {
  const detailParams = {
    // Details also reads `id` for rollback actions; this route's identifier is the work-order ID.
    id: record.maintenanceId,
    maintenanceId: record.maintenanceId,
    requestNo: record.requestNo,
    createByType: record.createByType,
    propUnit: record.propUnit,
    address: record.address,
    city: record.city,
    job: record.job,
    remark: record.remark,
  };
  const query = new URLSearchParams(Object.entries(detailParams)
    .filter(([, value]) => value !== null && value !== undefined && value !== ""));
  return `${window.location.origin}#/AccountPayable/MaintenancePayContractor/Details?${query.toString()}`;
};

const money = (value) => {
  const amount = Number(value || 0);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
};

const listFrom = (response) =>
  Array.isArray(response) ? response : response?.data || [];

/**
 * The overview remains one concise total per metric. Source detail follows the
 * metric: Receivables is tenant-grain, while bank-backed metrics group by trust.
 */
const CashPositionReport = () => {
  const [form] = Form.useForm();
  const [owners, setOwners] = useState([]);
  const [trusts, setTrusts] = useState([]);
  const [bankNames, setBankNames] = useState([]);
  const [report, setReport] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedTrusts, setExpandedTrusts] = useState(new Set());
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    // Report-specific options exclude inactive owners while retaining Common's label format.
    http.get(api.getCashPositionOwners)
      .then((response) => setOwners(listFrom(response).map((owner) => ({
        value: owner.ownerId,
        label: owner.titleName || owner.ownerName || owner.ownerCode || owner.ownerId,
      }))))
      .catch(() => message.error("Unable to load owners."));
  }, []);

  const ownerId = Form.useWatch("ownerId", form);
  const trustIds = Form.useWatch("trustIds", form) || EMPTY_LIST;

  useEffect(() => {
    if (!ownerId) {
      setTrusts([]); setBankNames([]); return;
    }
    // Owner changes invalidate downstream selections: keeping them would silently narrow All.
    form.setFieldsValue({ trustIds: [], bankNames: [] });
    setReport(null); setDetail(null);
    setTrusts([]);
    http.get(api.getCashPositionTrusts, { params: { ownerId } })
      .then((response) => setTrusts(listFrom(response).map((trust) => ({
        value: trust.trustId,
        label: trust.trustNameTitle || trust.trustName || trust.trust || trust.trustId,
        // Keep the dropdown descriptive, but selected tags compact.
        selectedLabel: String(trust.trust ?? "").trim() || String(trust.trustId),
      }))))
      .catch(() => message.error("Unable to load trusts."));
  }, [ownerId, form]);

  useEffect(() => {
    if (!ownerId) return;
    http.get(api.getCashPositionBankNames, {
      params: { ownerId, trustIds },
      paramsSerializer: { indexes: null },
    })
      .then((response) => setBankNames((response?.data || response || []).map((item) => ({ value: item.code, label: item.code }))))
      .catch(() => message.error("Unable to load bank names."));
  }, [ownerId, trustIds]);

  const payload = () => ({
    ownerId: form.getFieldValue("ownerId"),
    trustIds: form.getFieldValue("trustIds") || [],
    bankNames: form.getFieldValue("bankNames") || [],
  });

  const runOverview = async () => {
    try {
      await form.validateFields();
      setLoading(true); setDetail(null);
      const response = await http.post(api.getCashPositionOverview, payload());
      if (response?.code !== "1") throw new Error(response?.msg || "Unable to load Cash Position.");
      setReport(response.data);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.message || "Unable to load Cash Position.");
    } finally { setLoading(false); }
  };

  const openDetail = async (metric) => {
    if (CALCULATED_METRICS.has(metric.key)) {
      setExpandedTrusts(new Set());
      setDetail(calculatedDetail(metric));
      return;
    }
    try {
      setDetailLoading(true); setExpandedTrusts(new Set());
      const response = await http.post(api.getCashPositionDetail, { ...payload(), metric: metric.key });
      if (response?.code !== "1") throw new Error(response?.msg || "Unable to load metric detail.");
      setDetail(response.data);
    } catch (error) {
      message.error(error?.message || "Unable to load metric detail.");
    } finally { setDetailLoading(false); }
  };

  const calculatedDetail = (metric) => {
    const metricByKey = (key) => report?.metrics?.find((item) => item.key === key);
    const amountOf = (key) => Number(metricByKey(key)?.amount || 0);
    const labelOf = (key) => metricByKey(key)?.label || key;
    const endingBankBalance = amountOf("ENDING_BANK_BALANCE");
    const receivables = amountOf("RECEIVABLES");
    const outstandingChecks = Math.abs(amountOf("OUTSTANDING_CHECKS"));
    const endingBeforePayables = endingBankBalance + receivables - outstandingChecks;
    const formulaRows = metric.key === "ENDING_BALANCE_BEFORE_PAYABLES" ? [
      { sign: "", label: labelOf("ENDING_BANK_BALANCE"), amount: endingBankBalance },
      { sign: "+", label: labelOf("RECEIVABLES"), amount: receivables },
      { sign: "−", label: labelOf("OUTSTANDING_CHECKS"), amount: outstandingChecks },
    ] : [
      { sign: "", label: labelOf("ENDING_BALANCE_BEFORE_PAYABLES"), amount: endingBeforePayables },
      { sign: "−", label: labelOf("APPROVED_ACCOUNTS_PAYABLES"), amount: Math.abs(amountOf("APPROVED_ACCOUNTS_PAYABLES")) },
      { sign: "−", label: labelOf("PENDING_ACCOUNTS_PAYABLES"), amount: Math.abs(amountOf("PENDING_ACCOUNTS_PAYABLES")) },
      { sign: "−", label: labelOf("MAINTENANCE_PO_PAYABLES"), amount: Math.abs(amountOf("MAINTENANCE_PO_PAYABLES")) },
      { sign: "−", label: labelOf("MAINTENANCE_MATERIAL_PAYABLES"), amount: Math.abs(amountOf("MAINTENANCE_MATERIAL_PAYABLES")) },
    ];
    const amount = metric.key === "ENDING_BALANCE_BEFORE_PAYABLES" ? endingBeforePayables
      : formulaRows.reduce((total, row, index) => index === 0 ? row.amount : total - row.amount, 0);
    return { metric: metric.key, label: metric.label, amount, formulaRows };
  };

  const selectedSummary = useMemo(() => [
    report?.ownerName,
    trustIds.length ? `${trustIds.length} Trust${trustIds.length > 1 ? "s" : ""}` : "All Trusts",
    (form.getFieldValue("bankNames") || []).length ? `${form.getFieldValue("bankNames").length} Bank Type${form.getFieldValue("bankNames").length > 1 ? "s" : ""}` : "All Bank Types",
  ].filter(Boolean).join(" · "), [report, trustIds, form]);

  const exportReport = async (format) => {
    if (!report?.metrics?.length) return;
    setExporting(format);
    try {
      const endpoint = format === "pdf" ? api.getCashPositionToPdf : api.getCashPositionToExcel;
      // Send the loaded overview back as the export source so no long-running report SQL is repeated.
      const blob = await http.post(endpoint, {
        selectionSummary: selectedSummary,
        metrics: report.metrics.map(({ key, label, amount }) => ({ key, label, amount })),
      }, { responseType: "blob" });
      if (!(blob instanceof Blob)) throw new Error("Invalid file response");
      if (blob.type.includes("application/json")) {
        throw new Error(JSON.parse(await blob.text())?.msg || "Export failed");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Cash_Position_${dayjs().format("YYYYMMDD_HHmmss")}.${format === "excel" ? "xlsx" : "pdf"}`;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      message.error(error?.message || "Export failed.");
    } finally {
      setExporting("");
    }
  };

  const toggleTrust = (trustId) => setExpandedTrusts((previous) => {
    const next = new Set(previous);
    if (next.has(trustId)) next.delete(trustId); else next.add(trustId);
    return next;
  });

  const metricColumns = [
    {
      title: "Description",
      dataIndex: "label",
      key: "label",
      render: (label) => <span className="cash-position-description">{label}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 150,
      render: (amount, metric) => (
        <button
          className="cash-position-amount-link"
          type="button"
          onClick={() => openDetail(metric)}
        >
          {money(amount)}
        </button>
      ),
    },
  ];

  const receivableColumns = [
    { title: "Tenant", dataIndex: "tenantLabel", key: "tenantLabel", width: 170 },
    { title: "Address", dataIndex: "address", key: "address", width: 220, render: (value) => value || "—" },
    { title: "City", dataIndex: "city", key: "city", width: 140, render: (value) => value || "—" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 125,
      render: (amount) => <span className="cash-position-detail-amount">{money(amount)}</span>,
    },
  ];

  const maintenanceColumns = [
    {
      title: "Request No.",
      dataIndex: "requestNo",
      key: "requestNo",
      width: 225,
      render: (requestNo, row) => row.maintenanceId ? <a className="cash-position-request-link" href={maintenanceDetailHref(row)} target="_blank" rel="noopener noreferrer" title="Open maintenance work order">{requestNo}<span aria-hidden="true">↗</span></a> : requestNo,
    },
    { title: "Bank Account", dataIndex: "accountLabel", key: "accountLabel", width: 165 },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 125,
      render: (amount) => <span className="cash-position-detail-amount">{money(amount)}</span>,
    },
  ];

  const paymentColumns = detail?.metric === "OUTSTANDING_CHECKS" ? [
    { title: "Check No.", dataIndex: "checkNo", key: "checkNo", width: 170, render: (value) => value || "—" },
    { title: "Check Date", dataIndex: "checkDate", key: "checkDate", width: 160, render: displayDate },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 145, align: "right", render: (value) => <span className="cash-position-detail-amount">{money(value)}</span> },
  ] : [
    { title: "Invoice Number", dataIndex: "invoice", key: "invoice", width: 170, render: (value) => value || "—" },
    { title: "Invoice Date", dataIndex: "invoiceDate", key: "invoiceDate", width: 160, render: displayDate },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 145, align: "right", render: (value) => <span className="cash-position-detail-amount">{money(value)}</span> },
  ];

  // Namespaced expansion keys keep type/vendor and supervisor IDs independent.
  const groupHeading = (key, label, countLabel, amount) => <button type="button" aria-expanded={expandedTrusts.has(key)} onClick={() => toggleTrust(key)}>
    <DownOutlined className={expandedTrusts.has(key) ? "open" : ""} />
    <span><b>{label}</b><small>{countLabel}</small></span><strong>{money(amount)}</strong>
  </button>;

  const renderSupervisors = () => {
    const groups = detail?.supervisors || EMPTY_LIST;
    if (!groups.length) return <Empty description="No records match this metric." />;
    return <div className="cash-position-tree cash-position-source-tree">
      {groups.map((group) => {
        const key = `supervisor:${group.supervisorId ?? "unassigned"}`;
        const rows = group.receivables || EMPTY_LIST;
        return <div className="cash-position-trust" key={key}>
          {groupHeading(key, group.supervisorName, `${rows.length} Tenant${rows.length === 1 ? "" : "s"}`, group.amount)}
          {expandedTrusts.has(key) && <div className="cash-position-source-table"><Table rowKey="tenantId" columns={receivableColumns} dataSource={rows} pagination={false} size="small" scroll={{ x: 655 }} /></div>}
        </div>;
      })}
    </div>;
  };

  const renderPaymentTypes = () => {
    const types = detail?.paymentTypes || EMPTY_LIST;
    if (!types.length) return <Empty description="No records match this metric." />;
    return <div className="cash-position-tree cash-position-source-tree">
      {types.map((type) => {
        const key = JSON.stringify(["type", type.type]);
        const vendors = type.vendors || EMPTY_LIST;
        return <div className="cash-position-trust" key={key}>
          {groupHeading(key, type.typeLabel, `${vendors.length} Vendor${vendors.length === 1 ? "" : "s"}`, type.amount)}
          {expandedTrusts.has(key) && <div className="cash-position-vendor-list">
            {vendors.map((vendor) => {
              const vendorKey = JSON.stringify(["vendor", type.type, vendor.vendorId]);
              const rows = vendor.payments || EMPTY_LIST;
              return <div className="cash-position-trust cash-position-vendor" key={vendorKey}>
                {groupHeading(vendorKey, vendor.vendorLabel, `${(vendor.vendorTypeLabel || vendor.vendorType || "").trim() || "-"} · ${rows.length} Bill${rows.length === 1 ? "" : "s"}`, vendor.amount)}
                {expandedTrusts.has(vendorKey) && <div className="cash-position-source-table"><Table rowKey="vendorPaymentId" columns={paymentColumns} dataSource={rows} pagination={false} size="small" scroll={{ x: 475 }} /></div>}
              </div>;
            })}
          </div>}
        </div>;
      })}
    </div>;
  };

  const renderSourceTrusts = () => {
    const trustsWithSources = detail?.sourceTrusts || EMPTY_LIST;
    if (!trustsWithSources.length) return <Empty description="No records match this metric." />;
    return <div className="cash-position-tree cash-position-source-tree">
      {trustsWithSources.map((trust) => {
        const open = expandedTrusts.has(trust.trustId);
        const rows = trust.maintenancePayables || EMPTY_LIST;
        const countLabel = "Request";
        return <div className="cash-position-trust" key={trust.trustId}>
          <button type="button" onClick={() => toggleTrust(trust.trustId)}><DownOutlined className={open ? "open" : ""} /><span><b>{trust.trustName}</b><small>{rows.length} {countLabel}{rows.length === 1 ? "" : "s"}</small></span><strong>{money(trust.amount)}</strong></button>
          {open && <div className="cash-position-source-table"><Table rowKey={(row, index) => `${row.maintenanceId}-${index}`} columns={maintenanceColumns} dataSource={rows} pagination={false} size="small" scroll={{ x: 515 }} /></div>}
        </div>;
      })}
    </div>;
  };

  return (
    <main className="cash-position-page">
      <section className={`cash-position-layout${detail || detailLoading ? " cash-position-layout-detail" : ""}`}>
        <Card className="cash-position-filter" title="Cash Position Report">
          <Text className="cash-position-filter-copy">Choose an owner first. Trust and bank type default to all available records.</Text>
          <Form form={form} layout="vertical">
            <Form.Item name="ownerId" label="Owner" rules={[{ required: true, message: "Select an owner" }]}>
              <Select showSearch optionFilterProp="label" options={owners} placeholder="Select owner" />
            </Form.Item>
            <Form.Item name="trustIds" label="Trust">
              <Select className="cash-position-trust-select" mode="multiple" allowClear disabled={!ownerId} options={trusts} optionLabelProp="selectedLabel" optionFilterProp="label" placeholder="All Trusts" />
            </Form.Item>
            <Form.Item name="bankNames" label="Bank Type">
              <Select mode="multiple" allowClear disabled={!ownerId} options={bankNames} placeholder="All Bank Types" maxTagCount="responsive" />
            </Form.Item>
            <Button type="primary" block icon={<ReloadOutlined />} loading={loading} onClick={runOverview}>Run Report</Button>
          </Form>
        </Card>

        <section className="cash-position-overview">
          <Card
            className="cash-position-result-card"
            title={
              <div className="cash-position-preview-title">
                <span className="cash-position-eyebrow">LIVE CASH VIEW</span>
                <h1>Cash Position</h1>
                <Text className="cash-position-preview-subtitle">
                  {report ? selectedSummary : "Run the report to see the current position."}
                </Text>
              </div>
            }
            extra={report && <Space>
              <Button icon={<FileExcelOutlined />} loading={exporting === "excel"} onClick={() => exportReport("excel")}>Excel</Button>
              <Button type="primary" icon={<FilePdfOutlined />} loading={exporting === "pdf"} onClick={() => exportReport("pdf")}>PDF</Button>
              <Button icon={<ReloadOutlined />} onClick={runOverview}>Refresh</Button>
            </Space>}
          >
            {loading ? <div className="cash-position-state"><Spin size="large" /></div> : report ? (
              <div className="cash-position-table-host">
                <Table
                  className="cash-position-table"
                  rowKey="key"
                  rowClassName={(metric) => BALANCE_METRICS.has(metric.key)
                    ? `cash-position-balance-row${metric.key === "BEGIN_BANK_BALANCE" ? "" : " cash-position-balance-divider"}` : ""}
                  columns={metricColumns}
                  dataSource={report.metrics || []}
                  pagination={false}
                  size="small"
                  tableLayout="fixed"
                />
              </div>
            ) : <Empty className="cash-position-state" description="No preview yet" />}
          </Card>
        </section>

        {(detail || detailLoading) && <aside className="cash-position-detail">
          <Button className="cash-position-detail-collapse" type="text" icon={<DoubleRightOutlined />} onClick={() => setDetail(null)} title="Hide details" aria-label="Hide details" />
          <div className="cash-position-detail-head"><div><span className="cash-position-eyebrow">DETAIL</span><h2>{detail?.label || "Loading detail"}</h2><strong>{money(detail?.amount)}</strong></div></div>
          {detailLoading ? <div className="cash-position-state"><Spin /></div> : detail?.formulaRows ? <div className="cash-position-formula">
            <span className="cash-position-formula-caption">CALCULATED FROM THE CURRENT OVERVIEW</span>
            {detail.formulaRows.map((row, index) => <div className="cash-position-formula-row" key={row.label}>
              <span className="cash-position-formula-sign">{index === 0 ? "=" : row.sign}</span><span>{row.label}</span><strong>{money(row.amount)}</strong>
            </div>)}
            <div className="cash-position-formula-total"><span>{detail.label}</span><strong>{money(detail.amount)}</strong></div>
          </div> : detail?.metric === "RECEIVABLES" ? renderSupervisors() : PAYABLE_METRICS.has(detail?.metric) ? renderPaymentTypes() : MAINTENANCE_METRICS.has(detail?.metric) ? renderSourceTrusts() : detail?.trusts?.length ? <div className="cash-position-tree">
            {detail.trusts.map((trust) => { const open = expandedTrusts.has(trust.trustId); return <div className="cash-position-trust" key={trust.trustId}>
              <button type="button" onClick={() => toggleTrust(trust.trustId)}><DownOutlined className={open ? "open" : ""} /><span><b>{trust.trustName}</b><small>{trust.bankCount} Bank Account{trust.bankCount === 1 ? "" : "s"}</small></span><strong>{money(trust.amount)}</strong></button>
              {open && <div className="cash-position-bank-list">{trust.banks.map((bank) => <div className="cash-position-bank" key={bank.bankId}><BankOutlined /><span>{bank.bankLabel}</span><strong>{money(bank.amount)}</strong></div>)}</div>}
            </div>; })}
          </div> : <Empty description="No records match this metric." />}
        </aside>}
      </section>
    </main>
  );
};

export default CashPositionReport;
